import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, XIcon, TrendingUpIcon, FileTextIcon, UserIcon } from 'lucide-react';
interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
type SuggestionType = 'ARTICLE' | 'AUTHOR' | 'CATEGORY' | 'TRENDING';
interface Suggestion {
  id: string;
  title: string;
  type: SuggestionType;
  category?: string;
}
const MOCK_SUGGESTIONS: Suggestion[] = [{
  id: '1',
  title: 'The US agencies storming up the World Creative Rankings',
  type: 'ARTICLE',
  category: 'NEWS'
}, {
  id: '2',
  title: 'How MENA brands are redefining global advertising standards',
  type: 'ARTICLE',
  category: 'OPINION'
}, {
  id: '3',
  title: 'The rise of AI-powered creativity in the advertising industry',
  type: 'ARTICLE',
  category: 'INTERVIEW'
}, {
  id: '4',
  title: 'Top 10 Agencies in Dubai to watch in 2024',
  type: 'ARTICLE',
  category: 'NEWS'
}, {
  id: '5',
  title: 'Future of Digital Marketing and Web3',
  type: 'ARTICLE',
  category: 'FOCUS'
}, {
  id: '6',
  title: 'Brand Strategy Masterclass: Building trust',
  type: 'ARTICLE',
  category: 'EDITIONS'
}, {
  id: '7',
  title: 'Creative Leadership in Times of Crisis',
  type: 'ARTICLE',
  category: 'OPINION'
}, {
  id: '8',
  title: 'WPP announces latest strategic acquisition',
  type: 'ARTICLE',
  category: 'NEWS'
}, {
  id: '9',
  title: 'Omnicom Media Group leadership updates',
  type: 'ARTICLE',
  category: 'NEWS'
}, {
  id: '10',
  title: 'Publicis Groupe Q3 Financial Results',
  type: 'ARTICLE',
  category: 'NEWS'
}, {
  id: '11',
  title: 'Hubert Boulos',
  type: 'AUTHOR'
}, {
  id: '12',
  title: 'Tarek Miknas',
  type: 'AUTHOR'
}, {
  id: '13',
  title: 'Sarah Johnson',
  type: 'AUTHOR'
}, {
  id: '14',
  title: 'Digital Marketing',
  type: 'CATEGORY'
}, {
  id: '15',
  title: 'Creative Awards',
  type: 'CATEGORY'
}];
const TRENDING_SEARCHES: Suggestion[] = [{
  id: 't1',
  title: 'World Creative Rankings',
  type: 'TRENDING'
}, {
  id: 't2',
  title: 'MENA Advertising',
  type: 'TRENDING'
}, {
  id: 't3',
  title: 'AI in Marketing',
  type: 'TRENDING'
}, {
  id: 't4',
  title: 'Cannes Lions 2024',
  type: 'TRENDING'
}];
export function SearchOverlay({
  isOpen,
  onClose
}: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the input is rendered and animation started
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setQuery(''); // Reset query on close
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const filteredSuggestions = query.trim() === '' ? [] : MOCK_SUGGESTIONS.filter(item => item.title.toLowerCase().includes(query.toLowerCase()) || item.category?.toLowerCase().includes(query.toLowerCase())).slice(0, 8); // Limit to 8 results
  const getIconForType = (type: SuggestionType) => {
    switch (type) {
      case 'ARTICLE':
        return <FileTextIcon className="w-4 h-4" style={{
          color: '#6B6B6B'
        }} data-id="element-127" />;
      case 'AUTHOR':
        return <UserIcon className="w-4 h-4" style={{
          color: '#6B6B6B'
        }} data-id="element-128" />;
      case 'TRENDING':
        return <TrendingUpIcon className="w-4 h-4" style={{
          color: '#6B6B6B'
        }} data-id="element-129" />;
      default:
        return <SearchIcon className="w-4 h-4" style={{
          color: '#6B6B6B'
        }} data-id="element-130" />;
    }
  };
  return <AnimatePresence data-id="element-131">
      {isOpen && <div className="fixed inset-0 z-50 flex flex-col items-center pt-[10vh] md:pt-[15vh] px-4" data-id="element-132">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-133" />

          {/* Search Container */}
          <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-3xl z-10 flex flex-col gap-4" data-id="element-134">
            {/* Close Button (Mobile mostly, but good for desktop too) */}
            <div className="flex justify-end mb-2" data-id="element-135">
              <button onClick={onClose} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors" aria-label="Close search" data-id="element-136">
                <XIcon className="w-6 h-6" data-id="element-137" />
              </button>
            </div>

            {/* Input Wrapper */}
            <div className="flex items-center bg-white w-full shadow-2xl overflow-hidden" style={{
          borderRadius: '25px',
          height: '64px'
        }} data-id="element-138">
              <div className="pl-6 pr-3 flex items-center justify-center" data-id="element-139">
                <SearchIcon className="w-6 h-6" style={{
              color: '#6B6B6B'
            }} data-id="element-140" />
              </div>
              <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search articles, authors, categories..." className="flex-1 h-full bg-transparent border-none outline-none text-lg md:text-xl font-medium" style={{
            color: '#6B6B6B'
          }} data-id="element-141" />
              {query && <button onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }} className="pr-6 pl-3 flex items-center justify-center hover:opacity-70 transition-opacity" data-id="element-142">
                  <XIcon className="w-5 h-5" style={{
              color: '#6B6B6B'
            }} data-id="element-143" />
                </button>}
            </div>

            {/* Suggestions Box */}
            <div className="bg-white w-full shadow-2xl overflow-hidden flex flex-col" style={{
          borderRadius: '25px',
          maxHeight: '60vh'
        }} data-id="element-144">
              <div className="overflow-y-auto p-4 md:p-6" data-id="element-145">
                {query.trim() === '' ?
            // Trending Searches
            <div data-id="element-146">
                    <h3 className="text-sm font-bold uppercase tracking-wider mb-4 px-2" style={{
                color: '#6B6B6B'
              }} data-id="element-147">
                      Trending Searches
                    </h3>
                    <div className="flex flex-col gap-1" data-id="element-148">
                      {TRENDING_SEARCHES.map(item => <button key={item.id} className="flex items-center gap-4 w-full text-left px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors group" data-id="element-149">
                          <div className="bg-gray-100 p-2 rounded-full group-hover:bg-white transition-colors" data-id="element-150">
                            {getIconForType(item.type)}
                          </div>
                          <span className="font-medium text-lg" style={{
                    color: '#6B6B6B'
                  }} data-id="element-151">
                            {item.title}
                          </span>
                        </button>)}
                    </div>
                  </div> :
            // Filtered Results
            <div data-id="element-152">
                    <h3 className="text-sm font-bold uppercase tracking-wider mb-4 px-2" style={{
                color: '#6B6B6B'
              }} data-id="element-153">
                      {filteredSuggestions.length > 0 ? 'Suggestions' : 'No results found'}
                    </h3>
                    <div className="flex flex-col gap-1" data-id="element-154">
                      {filteredSuggestions.map(item => <button key={item.id} className="flex items-center gap-4 w-full text-left px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors group" data-id="element-155">
                          <div className="bg-gray-100 p-2 rounded-full group-hover:bg-white transition-colors flex-shrink-0" data-id="element-156">
                            {getIconForType(item.type)}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1" data-id="element-157">
                            <span className="font-medium text-lg truncate" style={{
                      color: '#6B6B6B'
                    }} data-id="element-158">
                              {item.title}
                            </span>
                          </div>
                          {item.category && <span className="hidden md:inline-block font-bold uppercase flex-shrink-0" style={{
                    border: '1px solid #D9D9D9',
                    borderRadius: '25px',
                    padding: '2px 12px',
                    color: '#6B6B6B',
                    fontSize: '11px',
                    letterSpacing: '0.05em'
                  }} data-id="element-159">
                              {item.category}
                            </span>}
                        </button>)}
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}